/**
 * Session traversal helpers (MUS-315).
 *
 * `planNextConfig` is the swappable, pure next-configuration chooser the
 * machine's NEXT transition calls - session-flow policy kept out of the
 * family-agnostic core so SP-03's ramp (or a future adaptive policy) is a
 * case here, not machine surgery. `takeKey` is the canonical (cell,
 * config) serialization the policy counters are keyed by (#128: counting
 * is per (cell, configuration), tempo included).
 */

import type { ScaleCell, ScaleConfig } from 'Types/ExerciseContentTypes';
import type { TraversalPolicy } from './exerciseSessionMachine.types';

/**
 * Canonical within-sitting counter key for one (cell, configuration).
 * Key-sorted so property order never splits a counter.
 */
export function takeKey(cell: ScaleCell, config: ScaleConfig): string {
  const sortEntries = (record: object): string =>
    Object.entries(record)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([key, value]) => `${key}=${String(value)}`)
      .join(',');
  return `${sortEntries(cell)}|${sortEntries(config)}`;
}

/**
 * Choose the next playthrough's configuration.
 *
 * @param current - the configuration just played
 * @param policy - the session's traversal policy (machine context)
 * @param clearedAtCurrent - whether the clearance bar has been met at
 *   `current` this sitting (#128: the ramp advances only when the bar is
 *   met at the current tempo)
 */
export function planNextConfig(
  current: ScaleConfig,
  policy: TraversalPolicy,
  clearedAtCurrent: boolean,
): ScaleConfig {
  switch (policy.kind) {
    case 'manual':
      return current;
    case 'ramp': {
      if (!clearedAtCurrent) return current;
      const value = (current as unknown as Record<string, unknown>)[policy.dimension];
      if (typeof value !== 'number') return current; // ramp only moves scalars
      const next = value + policy.step;
      if (policy.max !== undefined && next > policy.max) return current;
      return { ...current, [policy.dimension]: next } as ScaleConfig;
    }
  }
}
